import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { getMoviesGenresList, getTVGenresList } from "../services/home";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { useCurrentViewportView } from "../hooks/useCurrentViewportView";
import MobileNav from "../components/Common/MobileNav";
import Sidebar from "../components/Common/Sidebar";
import SearchBox from "../components/Common/SearchBox";
import PageTitle from "../components/Common/PageTitle";
import SearchResult from "../components/Search/SearchResult";
import Footer from "../components/Footer/Footer";

const Search = () => {
  const [isSidebarActive, setIsSidebarActive] = useState(false);
  const { isMobile } = useCurrentViewportView();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openSearchFilter, setOpenSearchFilter] = useState(true);
  const [parent] = useAutoAnimate();
  const query = searchParams.get("query");
  const page = searchParams.get("page") || 1;
  const [currentTab, setCurrentTab] = useState("multi");

  const {
    data: moviesGenres,
    isLoading: isLoadingMoviesGenres,
    isError: isErrorMoviesGenres,
    error: errorMoviesGenres,
  } = useQuery(["movies-genres"], getMoviesGenresList);

  const {
    data: tvGenres,
    isLoading: isLoadingTVGenres,
    isError: isErrorTVGenres,
    error: errorTVGenres,
  } = useQuery(["tv-genres"], getTVGenresList);

  // GENRES

  if (isErrorMoviesGenres) return <p>ERROR: ${errorMoviesGenres.message}</p>;

  if (isLoadingMoviesGenres) return <p>Loading...</p>;

  if (isErrorTVGenres) return <p>ERROR: ${errorTVGenres.message}</p>;

  if (isLoadingTVGenres) return <p>Loading...</p>;

  const FilterSearchResults = () => {
    return (
      <div className="shrink-0 w-[500px] md:w-[300px] max-w-[90vw] w-full py-8 pt-24 px-4">
        <div
          ref={parent}
          className="bg-dark-lighten rounded-md shadow-md px-4 pt-3"
        >
          <div
            className="flex justify-between items-center text-white pb-3 cursor-pointer"
            onClick={() => setOpenSearchFilter((prev) => !prev)}
          >
            <p className="text-lg ">Search Results</p>
            <button>
              {openSearchFilter && <FiChevronDown size={20} />}
              {!openSearchFilter && <FiChevronRight size={20} />}
            </button>
          </div>
          {openSearchFilter && (
            <div className="md:py-6 py-2 border-t border-dark-darken text-white text-lg flex md:flex-col flex-row gap-1 md:gap-3">
              <button
                onClick={() => {
                  setSearchParams({ query: query || "", page: "1" });
                  setCurrentTab("multi");
                }}
                className={`w-full flex justify-center md:justify-between hover:bg-gray-darken px-2.5 py-1 rounded-md transition duration-300 ${
                  currentTab === "multi" && "bg-gray-darken"
                }`}
              >
                <span>All</span>
              </button>
              <button
                onClick={() => {
                  setSearchParams({ query: query || "", page: "1" });
                  setCurrentTab("movie");
                }}
                className={`w-full flex justify-center md:justify-between hover:bg-gray-darken px-2.5 py-1 rounded-md transition duration-300 ${
                  currentTab === "movie" && "bg-gray-darken"
                }`}
              >
                <span>Movie</span>
              </button>
              <button
                onClick={() => {
                  setSearchParams({ query: query || "", page: "1" });
                  setCurrentTab("tv");
                }}
                className={`w-full flex justify-center md:justify-between hover:bg-gray-darken px-2.5 py-1 rounded-md transition duration-300 ${
                  currentTab === "tv" && "bg-gray-darken"
                }`}
              >
                <span>TVShow</span>
              </button>
              <button
                onClick={() => {
                  setSearchParams({ query: query || "", page: "1" });
                  setCurrentTab("person");
                }}
                className={`w-full flex justify-center md:justify-between hover:bg-gray-darken px-2.5 py-1 rounded-md transition duration-300 ${
                  currentTab === "person" && "bg-gray-darken"
                }`}
              >
                <span>People</span>
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {!query && <PageTitle value="Search | SHWETFLIX" />}
      {query && <PageTitle value={`Search: ${query} | SHWETFLIX`} />}

      <MobileNav setIsSidebarActive={setIsSidebarActive} />
      <div className="bg-dark-darken z-10">
        <div className="flex min-h-screen flex-col-reverse md:flex-row">
          <Sidebar
            setIsSidebarActive={setIsSidebarActive}
            isSidebarActive={isSidebarActive}
          />
          <div className="flex-grow border-l border-gray-darken min-h-screen px-[2vw]">
            <div
              className={`flex items-center justify-center z-30 md:max-w-[50vw] w-full mx-auto translate-y-[100px] transition duration-300 text-xl ${
                query && "!translate-y-0"
              }`}
            >
              <h1
                className={`text-white text-2xl font-medium text-center absolute mb-10 lg:mb-5 ${
                  query ? "opacity-0 invisible" : "opacity-100 visible"
                } transition duration-500`}
              >
                Find your favourite movies, TV shows, people and more
              </h1>
              <SearchBox autoFocus />
              {isMobile && query && <FilterSearchResults />}
            </div>
            {!query && (
              <div className="mt-[300px] flex justify-center">
                <LazyLoadImage
                  src="/search.svg"
                  alt="searhPic"
                  effect="opacity"
                  className="w-[25vw] min-w-[300px] object-cover rounded-xl "
                />
              </div>
            )}
            {query && (
              <SearchResult
                currentTab={currentTab}
                query={query}
                page={Number(page)}
                genresList={currentTab === "movie" ? moviesGenres : tvGenres}
              />
            )}
          </div>
          {!isMobile && query && <FilterSearchResults />}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Search;
